import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import React, { useCallback, useContext, useEffect } from 'react';
import classnames from 'classnames';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { useCommentPostAction } from '../../../CommentEditor/hooks/useCommentPostAction';
import { useCommentCancelAction } from '../../../CommentEditor/hooks/useCommentCancelAction';
import { CommentEditorContext } from '../../../CommentEditor/context';
import CommentEditor from '../../../CommentEditor';
import LoadingPlaceholder from '../LoadingPlaceholder';
import LazyPlaceholder from '../LazyPlaceholder';
import CommentBoxFooter from '../CommentBoxFooter';
import { useEditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useDispatch } from 'react-redux';
import { resetEditorPublishResults } from '../../../CommentEditor/redux/actionCreators';
import s from './CommentBoxEditor.scss';
/** The core of our comment box. This is a `<CommentEditor />` with a footer and a loading indicator */ const CommentBoxEditor = (_param)=>{
    var { className, footerClassName, nc, onCancel, onPost, onEditorReady } = _param, restProps = _object_without_properties(_param, [
        "className",
        "footerClassName",
        "nc",
        "onCancel",
        "onPost",
        "onEditorReady"
    ]);
    const { isBusy, editorId, commentableItemId, commentableType, isExpanded, shouldResetEditor } = useContext(CommentEditorContext);
    const { isLoadingTriggered } = useContext(LoadableComponentContext);
    const postComment = useCommentPostAction({
        messageId: nc === null || nc === void 0 ? void 0 : nc.messageId,
        folderId: nc === null || nc === void 0 ? void 0 : nc.folderId,
        shouldDeleteMessage: nc === null || nc === void 0 ? void 0 : nc.shouldDeleteMessage,
        onPost
    });
    const cancelComment = useCommentCancelAction({
        onCancel
    });
    // Listen for mod+enter submit events coming from the editor
    const onKeyPost = useCallback(()=>{
        postComment();
    }, [
        postComment
    ]);
    const editor = useEditorContext();
    useEffect(()=>{
        editor === null || editor === void 0 ? void 0 : editor.on('do-submit', onKeyPost);
        return ()=>{
            editor === null || editor === void 0 ? void 0 : editor.off('do-submit', onKeyPost);
        };
    }, [
        editor,
        onKeyPost
    ]);
    const dispatch = useDispatch();
    useEffect(()=>{
        if (shouldResetEditor) {
            dispatch(resetEditorPublishResults(editorId));
            cancelComment({
                bypassConfirm: true
            });
        }
    }, [
        editorId,
        dispatch,
        shouldResetEditor,
        cancelComment
    ]);
    return /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "CommentBoxEditor"
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['root'], isExpanded && s['expanded'], className)
    }, isBusy && /*#__PURE__*/ React.createElement(LoadingIndicator, {
        className: s['loading']
    }), !isLoadingTriggered && /*#__PURE__*/ React.createElement(LazyPlaceholder, null), isLoadingTriggered && /*#__PURE__*/ React.createElement(CommentEditor, _object_spread({
        className: classnames(s['commentbox-editor'], isExpanded && s['expanded']),
        loadingIndicator: /*#__PURE__*/ React.createElement(LoadingPlaceholder, null),
        onEditorReady: onEditorReady
    }, restProps)), isLoadingTriggered && /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            itemid: typeof commentableItemId === 'string' ? parseInt(commentableItemId) : commentableItemId,
            typeid: commentableType
        }
    }, /*#__PURE__*/ React.createElement(CommentBoxFooter, {
        onCancel: cancelComment,
        onPost: postComment,
        className: classnames(s['commentbox-footer'], footerClassName)
    })), nc && nc.postfix));
};
CommentBoxEditor.displayName = 'CommentBoxEditor';
export default CommentBoxEditor;

//# sourceMappingURL=CommentBoxEditor.js.map